import { useEffect, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import UnsavedChangesPrompt from "../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import AlarmScheduleEditorUtils, { AlarmScheduleAdminMode, AlarmScheduleFormType, IntervalType, OverrideDurationEnum, defaultAlarmScheduleForm } from "./AlarmScheduleEditorUtils";
import Form from "../../../components/forms/form/Form";
import { useTimezones } from "../../../queries/time-zones/UseTimezones";
import { useAlarmSchedule } from "../../../queries/alarm-schedule-admin/UseAlarmSchedule";
import SettingsBar, { SettingsActionEnum } from "../../../components/settings-bar/SettingsBar";
import FormProperty from "../../../components/forms/form-property/FormProperty";
import FormPropertySection from "../../../components/forms/form-property-section/FormPropertySection";
import FormInput from "../../../components/forms/form-input/FormInput";
import { useAlarmSchedules } from "../../../queries/alarm-schedule-admin/UseAlarmSchedules";
import AlarmScheduleInterval from "./AlarmScheduleInterval";
import FormSelect from "../../../components/forms/form-select/FormSelect";
import { Alert, MenuItem, Stack } from "@mui/material";
import FormCheckbox from "../../../components/forms/form-checkbox/FormCheckbox";
import BlackoutDayOptions from "./BlackoutDayOptions";
import BlackoutHourOptions from "./BlackoutHourOptions";
import BlackoutPreview from "./BlackoutPreview";
import FormRadioGroup, { FormRadioGroupRadioButton } from "../../../components/forms/form-radio-group/FormRadioGroup";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSaveAlarmSchedule } from "../../../queries/alarm-schedule-admin/UseSaveAlarmSchedule";
import { addDays } from "date-fns";
import DefaultDateTimePicker from "../../../components/default-date-time-picker/DefaultDateTimePicker";

export type AlarmScheduleEditorCloseType = {
    reason: "cancel" | "save";
    alarmScheduleID: number | undefined;
};

export type AlarmScheduleEditorProps = {
    organizationID: number,
    groupID: number,
    alarmScheduleID: number;
    
    onClose: ({ reason }: AlarmScheduleEditorCloseType) => void;
};

export default function AlarmScheduleEditor({
    organizationID,    
    groupID,
    alarmScheduleID,
    onClose,
}: AlarmScheduleEditorProps): JSX.Element {


    const { confirm
        , acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
  
    const { data: timezones } = useQueryNotifications(useTimezones)();    

    const mode = (alarmScheduleID ?? 0) <= 0 ? 
            AlarmScheduleAdminMode.Add : 
            AlarmScheduleAdminMode.Edit;

    const {data, status/*, refetch: refetchSchema*/ } = useQueryNotifications(useAlarmSchedule)({
        groupID,
        organizationID: organizationID,
        alarmScheduleID: alarmScheduleID,
        includeLinkedAlarmSchemes: false
    });

    const { data: schedules  } = useQueryNotifications(useAlarmSchedules)({
        groupID: groupID,
        organizationID: organizationID,
        includeLinkedAlarmSchemes: true,
        //enabled: organization !== undefined
    });


    const { mutate: saveAlarmSchedule } = useQueryNotifications(useSaveAlarmSchedule)({
        groupID: groupID,
        organizationID: organizationID,
        onSuccess: async (data) => {
            
            acknowledge(t(mode === AlarmScheduleAdminMode.Add ? 
                            "alarmSchedule.added" : 
                            "alarmSchedule.updated"
                        ));
            onClose({
                reason: "save",
                alarmScheduleID: data.id
            });
        }
    });

    const form = useForm<AlarmScheduleFormType>({
        mode: 'onChange',
        defaultValues: {...defaultAlarmScheduleForm}
    });

    const {
        getValues,
        watch,
        formState: {
            //dirtyFields,
            isDirty,
            isValid,
            isSubmitting
            //errors
            //defaultValues
            //touchedFields
        },
        reset,
        setValue,
        control,
        handleSubmit,
        trigger
    } = form;

    const hasChanges = isDirty && isValid;

    const alarmSchedule = useWatch({ control: control }) as AlarmScheduleFormType;

    const [name,
        startDate,
        endDate,
        setBlackoutHours,
        //setBlackoutDays,
        overrideDuration
    ] = watch([
        'name',
        'dateRange.startDate',
        'dateRange.endDate',
        'setBlackoutHours',
        //'setBlackoutDays',
        'overrideDuration'
    ]);


//    console.log(`isDirty: ${isDirty} and dirtyFields: ${JSON.stringify(dirtyFields)  } setBlackoutHours: ${setBlackoutHours} setBlackoutDays: ${setBlackoutDays}`);

    const radioButtons: FormRadioGroupRadioButton[] = [{
        label: <Stack direction='row'>
                    <AlarmScheduleInterval 
                        name='duration' 
                        onChange={onDurationChanged}
                        disabled={overrideDuration === OverrideDurationEnum.UseEndDate}
                    />
                </Stack>,
            //ariaLabel: t('alarmSchemeAdmin.tiers.everyXMinutes') as string,
            value: OverrideDurationEnum.UseDuration,
        }, {
            label: <Stack direction='row'>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DefaultDateTimePicker
                                    label={t("datetime.endTime")}
                                    value={endDate}
                                    minDate={addDays(startDate ?? new Date(), 1)}
                                    //maxDate={new Date()}
                                    slotProps={{ 
                                        //field: {clearable: true},
                                        textField: { variant: 'filled', sx: {
                                            //minWidth: 300
                                            //flexBasis: '50%',
                                            marginTop: 1,
                                            marginLeft: 1,
                                            marginRight: 2
                                        }} 
                                    }}
                                    onAccept={(date: Date | null) => onEndDateChanged(date)}
                                    disabled={overrideDuration === OverrideDurationEnum.UseDuration}
                                />
                                </LocalizationProvider>

                    

                </Stack>,
            ariaLabel: t("datetime.endTime") as string,
            value: OverrideDurationEnum.UseEndDate
        },
    ];

    async function onOkay(/*_formValues: AlarmScheduleFormType*/) {

        if (timezones) {
            const values = getValues();
            const model = AlarmScheduleEditorUtils.mapToModel(values, timezones);

            saveAlarmSchedule(model);
        }
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserExitConfirmation()
            : true;

        if (canCancel) {
            onClose({ 
                alarmScheduleID: alarmScheduleID,
                reason: "cancel" 
            });
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    function updateEndDate(start: Date, interval: IntervalType) {
        
        const end = AlarmScheduleEditorUtils.calculateEndDate(start, interval);

        setValue('dateRange.endDate', end, {
            shouldDirty: true
        });
    }

    function onDurationChanged() {

        const [value, dateRange] = getValues(['duration', 'dateRange']);
        updateEndDate(dateRange.startDate as Date, value);
    }

    function onRepeatIntervalChanged() {

    }

    function onStartDateChanged(value: Date | null) {
        if (value) {

            const interval = getValues('duration');

            setValue('dateRange.startDate', value, {shouldDirty: true});
            updateEndDate(value, interval);
        }
    }

    function onEndDateChanged(value: Date | null) {
        if (value) {

            setValue('dateRange.endDate', value, {shouldDirty: true});

            if (overrideDuration === OverrideDurationEnum.UseEndDate && startDate) {
    
                // how can you consistently calculate from something like 11/1/2023 11:00 am and 11/2/2023 1:23 pm?
                const newDuration = AlarmScheduleEditorUtils.calculateDuration(startDate, value);
    
                if (newDuration) {
                    setValue('duration', newDuration, {shouldDirty: true});
                }
            }
        }
    }

    function onBlackoutDaysChanged(checked: boolean) {
        if (!checked) {

            const blackoutSchedule = getValues(`blackoutSchedules.${0}`);
            blackoutSchedule.sunday = false;
            blackoutSchedule.monday = false;
            blackoutSchedule.tuesday = false;
            blackoutSchedule.wednesday = false;
            blackoutSchedule.thursday = false;
            blackoutSchedule.friday = false;
            blackoutSchedule.saturday = false;
            setValue(`blackoutSchedules.${0}`, blackoutSchedule, {shouldDirty: true});            
        }
    }


    const _validateName = (value: string): boolean => {
        return value.toString().trim().length > 0;
    }


    const _validateNameLength = (value: string): boolean => {

        return value.toString().trim().length <= 64;
    }

    const _validateNameUnique = (value: string): boolean => {

        return schedules?.find(o => o.id !== (alarmSchedule?.id ?? 0) && o.name.toLowerCase() === value) === undefined;
    }
    
    useEffect(() => {

        if (data && timezones) {
            const timezoneIndex = timezones.find(o => o.standardName === data.timezone)?.id;

            reset(AlarmScheduleEditorUtils.mapToFormValues(data, 
                timezoneIndex ?? 20, 
                timezones
            ));
        }
    }, [
        data,
        timezones,
        //userState.user,
        reset,
    ]);

    // execute on every render
    useEffect(() => {

        setTimeout(() => {
            const items: ('name' | 'blackoutSchedules')[] = [];

            items.push('blackoutSchedules');

            if ((name?.trim().length ?? 0) === 0) {
                items.push('name');
            }

            if (items.length) {
                trigger(items);
            }

        }, 1);
    });

    return (
        <LoadingZone isLoading={status === 'loading'}>
            <SettingsBar 
                title={t(mode === AlarmScheduleAdminMode.Add ? 'buttons.addAlarmSchedule' : 'buttons.editAlarmSchedule')  } 
                
                onClose={
                    async (reason: SettingsActionEnum) => {
                        if (reason === SettingsActionEnum.OK) {
                            handleSubmit(async (/*data*/) => onOkay(/*data*/))();
                        } else {
                            onCancel();
                        }
                    }}
                modified={isDirty && isValid && !isSubmitting} />
            {data && 

            <Form form={form} >

                <FormPropertySection label={t('properties.standard')}>

                    <FormProperty>
                        <FormInput
                            name="name"
                            label={t('properties.name')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('alarmSchedule.nameRequired')
                                },
                                validate: (value: string) => 
                                !_validateName(value) ?
                                    t('alarmSchedule.nameRequired') : 
                                !_validateNameLength(value) ?
                                    t('alarmSchedule.nameLength') : 
                                !_validateNameUnique(value) ?
                                    t('alarmSchedule.nameUnique') : 
                                true
                            }}

                        />
                    </FormProperty>

                    <FormProperty>
                        <FormSelect
                            name='timezoneIndex'
                            label={t('properties.timezone')}
                            sx={{ 
                                width: 1,
                                marginTop: 1,
                                marginLeft: 1
                            }}
                            
                        >
                            {timezones?.map(o => (
                                <MenuItem key={o.id} value={o.id}>
                                    {o.name}
                                </MenuItem>
                            ))}
                            
                        </FormSelect>
                    </FormProperty>

                </FormPropertySection>

                <FormPropertySection label=''>

                    <Stack direction='row' sx={{
                        flexWrap: 'wrap'
                    }}>
                    
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DefaultDateTimePicker
                                label={t("datetime.startTime")}
                                value={startDate}
                                //maxDate={new Date()}
                                slotProps={{ 
                                    textField: { variant: 'filled', sx: {
                                        marginTop: 1,
                                        marginLeft: 1,
                                        marginRight: 2
                                    }} 
                                }}
                                onAccept={(date: Date | null) => onStartDateChanged(date)}
                                onChange={(date: Date | null) => onStartDateChanged(date)}
                            />
                            </LocalizationProvider>

                    <FormRadioGroup label=''
                        name='overrideDuration'
                        radioButtons={radioButtons} 
                        direction="row"
                    />
                        <AlarmScheduleInterval name='repeat' onChange={onRepeatIntervalChanged}/>

                    </Stack>        

                </FormPropertySection>

                <FormPropertySection label={t('alarmSchedule.blackoutOptions')}>

                    <Alert severity='info' sx={{width: '100%'}}>
                        {t('alarmSchedule.blackoutInfo')}
                    </Alert>

                    <FormProperty>
                        <Stack direction='column'>
                            <FormCheckbox
                                name={`setBlackoutHours`}
                                label={t('alarmSchedule.setBlackoutHours')}
                                sx={{ 
                                    marginRight: 0,
                                    marginTop: 0,
                                }}
                                typographyProps={{
                                    sx: {
                                        fontSize: '.95rem',
                                    }
                                }}
                            />

                            {/* the current requirements only specify one black out config */}
                            <Stack direction='row'>

                                <BlackoutHourOptions
                                    name={`blackoutSchedules.${0}.startHour`}
                                    disabled={!setBlackoutHours}
                                    label={t('properties.startTime')}
                                />
                                <BlackoutHourOptions
                                    name={`blackoutSchedules.${0}.endHour`}
                                    disabled={!setBlackoutHours}
                                    label={t('properties.endTime')}
                                />
                            </Stack>
                        </Stack>

                    </FormProperty>

                    <FormProperty>

                        <Stack direction='column'>

                            <FormCheckbox
                                name={`setBlackoutDays`}
                                label={t('alarmSchedule.setBlackoutDays')}
                                sx={{ 
                                    marginRight: 0,
                                    marginTop: 0,
                                }}
                                typographyProps={{
                                    sx: {
                                        fontSize: '.95rem',
                                    }
                                }}
                                onChange={(_ev, checked) => onBlackoutDaysChanged(checked)}
                            />

                            {/* the current requirements only specify one black out config */}
                            <BlackoutDayOptions index={0} />


                        </Stack>

                    </FormProperty>
                </FormPropertySection>

                <FormPropertySection label={t('alarmSchedule.blackoutPreview')}>

                    <BlackoutPreview />

                </FormPropertySection>

            </Form>
            }
            <UnsavedChangesPrompt
                hasChanges={hasChanges}
                getUserConfirmation={getUserExitConfirmation}
            />
        </LoadingZone>
    );
}
